import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Modal, Input } from '../components/ui';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDatabase } from '../hooks/useDatabase';
import { supabase } from '../supabase/supabase';

const EMPTY = {
  cliente_id: '',
  trastero_id: '',
  concepto: '',
  importe: '',
  metodo_pago: 'transferencia',
  estado: 'pendiente',
  fecha_vencimiento: '',
  fecha_pago: '',
  observaciones: ''
};

const Pagos = () => {
  const { data, loading, error, refresh, insert, update, remove } = useDatabase('pagos');
  const { data: clientes } = useDatabase('clientes');
  const { data: trasteros } = useDatabase('trasteros');
  const { data: reservas } = useDatabase('reservas'); // Necesario para saber quién está activo
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('todos'); // 'todos', 'pendientes', 'activos'

  // Efecto para actualizar automáticamente pagos vencidos a "atrasado"
  useEffect(() => {
    const checkOverduePayments = async () => {
      const today = new Date().toISOString().split('T')[0];
      
      const overduePayments = data.filter(p => 
        p.estado === 'pendiente' && 
        p.fecha_vencimiento && 
        p.fecha_vencimiento < today
      );

      if (overduePayments.length > 0) {
        try {
          const ids = overduePayments.map(p => p.id);
          
          const { error: updateError } = await supabase
            .from('pagos_20240520')
            .update({ estado: 'atrasado' })
            .in('id', ids);
            
          if (!updateError) {
            refresh();
          } else {
            console.error('Error de Supabase al actualizar pagos:', updateError);
          }
        } catch (err) {
          console.error('Error actualizando pagos atrasados:', err);
        }
      }
    };

    if (data && data.length > 0 && !loading) {
      checkOverduePayments();
    }
  }, [data, loading, refresh]);

  const totalPagado = data.filter(p => p.estado === 'pagado').reduce((s, p) => s + Number(p.importe), 0);
  const totalPendiente = data.filter(p => p.estado === 'pendiente' || p.estado === 'atrasado').reduce((s, p) => s + Number(p.importe), 0);

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      cliente_id: item.cliente_id || '',
      trastero_id: item.trastero_id || '',
      concepto: item.concepto,
      importe: item.importe,
      metodo_pago: item.metodo_pago,
      estado: item.estado,
      fecha_vencimiento: item.fecha_vencimiento || '',
      fecha_pago: item.fecha_pago || '',
      observaciones: item.observaciones || ''
    });
    setEditingId(item.id);
    setIsModalOpen(true);
  };

  const openGenerate = (reserva, trastero, cliente) => {
    const today = new Date();
    const month = today.toLocaleString('es-ES', { month: 'long' });
    const year = today.getFullYear();
    const concepto = `Mensualidad ${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;

    setForm({
      cliente_id: reserva.cliente_id,
      trastero_id: reserva.trastero_id,
      concepto: concepto,
      importe: trastero.precio_mensual || '',
      metodo_pago: 'transferencia',
      estado: 'pendiente',
      fecha_vencimiento: today.toISOString().split('T')[0],
      fecha_pago: '',
      observaciones: ''
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        importe: parseFloat(form.importe),
        cliente_id: form.cliente_id || null,
        trastero_id: form.trastero_id || null,
        fecha_pago: form.fecha_pago || null,
        fecha_vencimiento: form.fecha_vencimiento || null
      };

      if (editingId) await update(editingId, payload);
      else await insert(payload);
      
      setIsModalOpen(false);
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este pago?')) return;
    try {
      await remove(id);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const clienteNombre = (id) => clientes.find(c => c.id === id)?.nombre_completo || '—';

  const sortedTrasteros = [...trasteros].sort((a,b) => (a.codigo || '').localeCompare(b.codigo || '', 'es', {numeric: true}));
  const sortedClientes = [...clientes].sort((a,b) => (a.nombre_completo || '').localeCompare(b.nombre_completo || '', 'es'));

  // Datos filtrados para la tabla de pagos
  const displayedPagos = activeTab === 'pendientes' 
    ? data.filter(p => p.estado === 'pendiente' || p.estado === 'atrasado')
    : data;

  // Clientes activos para la pestaña de "Generar"
  const activeReservations = reservas.filter(r => r.estado === 'ocupada' || r.estado === 'confirmada');

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark flex items-center gap-2">
            Control de Pagos
            {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-lg" />}
          </h2>
          <p className="text-sm sm:text-base text-brand-text">Gestión de cobros, pendientes y mensualidades.</p>
        </div>
        <Button onClick={openNew} className="w-full sm:w-auto flex justify-center items-center gap-2">
          <SafeIcon icon={FiIcons.FiPlus} />
          Registrar Pago Manual
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3 text-sm">
          <SafeIcon icon={FiIcons.FiAlertTriangle} /><span>Error: {error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
        <Card className="p-4 border-l-4 border-brand-accent">
          <p className="text-sm text-brand-text">Total Cobrado</p>
          <h3 className="text-2xl font-bold text-brand-dark">€ {totalPagado.toFixed(2)}</h3>
        </Card>
        <Card className="p-4 border-l-4 border-brand-alert">
          <p className="text-sm text-brand-text">Pendiente de Cobro</p>
          <h3 className="text-2xl font-bold text-brand-dark">€ {totalPendiente.toFixed(2)}</h3>
        </Card>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-100">
          <div className="flex overflow-x-auto w-full sm:w-auto hide-scrollbar">
            <button 
              className={`py-3 px-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${activeTab === 'todos' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('todos')}
            >
              Todos los Pagos
            </button>
            <button 
              className={`py-3 px-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${activeTab === 'pendientes' ? 'border-brand-alert text-brand-alert' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('pendientes')}
            >
              Pendientes / Atrasados
            </button>
            <button 
              className={`py-3 px-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${activeTab === 'activos' ? 'border-brand-accent text-brand-accent' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('activos')}
            >
              Clientes Activos (Generar Cobro)
            </button>
          </div>
          <div className="p-2 w-full sm:w-auto flex justify-end">
            <Button variant="ghost" onClick={refresh} className="w-full sm:w-auto flex justify-center">
              <SafeIcon icon={FiIcons.FiRefreshCw} className={loading ? 'animate-spin' : ''} />
              <span className="ml-2 sm:hidden">Actualizar</span>
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto hide-scrollbar">
          {activeTab === 'activos' ? (
            // TABLA DE CLIENTES ACTIVOS PARA GENERAR COBROS
            <table className="w-full text-left text-sm min-w-[600px]">
              <thead className="bg-gray-50 text-brand-text">
                <tr>
                  <th className="px-6 py-3 font-medium">Cliente</th>
                  <th className="px-6 py-3 font-medium">Trastero</th>
                  <th className="px-6 py-3 font-medium">Precio Mensual</th>
                  <th className="px-6 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeReservations.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-brand-text py-12 text-sm">No hay clientes con reservas activas.</td>
                  </tr>
                ) : (
                  activeReservations.map(res => {
                    const trastero = trasteros.find(t => t.id === res.trastero_id);
                    const cliente = clientes.find(c => c.id === res.cliente_id);
                    if (!cliente || !trastero) return null;
                    
                    return (
                      <tr key={res.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-brand-dark">{cliente.nombre_completo}</td>
                        <td className="px-6 py-4 text-brand-text">{trastero.codigo} - {trastero.zona}</td>
                        <td className="px-6 py-4 font-bold text-brand-dark">€{Number(trastero.precio_mensual || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-right">
                          <Button 
                            className="text-xs py-1 px-3 bg-brand-accent hover:bg-teal-600 text-white" 
                            onClick={() => openGenerate(res, trastero, cliente)}
                          >
                            <SafeIcon icon={FiIcons.FiFilePlus} className="mr-1 inline" />
                            Generar Recibo
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          ) : (
            // TABLA DE PAGOS (TODOS O PENDIENTES)
            !loading && displayedPagos.length === 0 ? (
              <p className="text-center text-brand-text py-12 text-sm">
                {activeTab === 'pendientes' ? 'No hay pagos pendientes de cobro.' : 'No hay pagos registrados.'}
              </p>
            ) : (
              <table className="w-full text-left text-sm min-w-[600px]">
                <thead className="bg-gray-50 text-brand-text">
                  <tr>
                    <th className="px-6 py-3 font-medium">Concepto</th>
                    <th className="px-6 py-3 font-medium">Cliente</th>
                    <th className="px-6 py-3 font-medium">Importe</th>
                    <th className="px-6 py-3 font-medium">Método</th>
                    <th className="px-6 py-3 font-medium">Vencimiento</th>
                    <th className="px-6 py-3 font-medium">Estado</th>
                    <th className="px-6 py-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayedPagos.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-brand-dark">{item.concepto}</td>
                      <td className="px-6 py-4 text-brand-text">{clienteNombre(item.cliente_id)}</td>
                      <td className="px-6 py-4 font-bold text-brand-dark">€{Number(item.importe).toFixed(2)}</td>
                      <td className="px-6 py-4 text-brand-text capitalize">{item.metodo_pago || '—'}</td>
                      <td className="px-6 py-4 text-brand-text">{item.fecha_vencimiento || '—'}</td>
                      <td className="px-6 py-4"><Badge variant={item.estado}>{item.estado}</Badge></td>
                      <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                        <button className="text-gray-400 hover:text-brand-primary p-2 sm:p-1" onClick={() => openEdit(item)}>
                          <SafeIcon icon={FiIcons.FiEdit2} />
                        </button>
                        <button className="text-gray-400 hover:text-brand-alert p-2 sm:p-1" onClick={() => handleDelete(item.id)}>
                          <SafeIcon icon={FiIcons.FiTrash2} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </Card>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }}
        title={editingId ? 'Editar Pago' : 'Registrar Pago'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Concepto" placeholder="Mensualidad Noviembre" value={form.concepto} onChange={e => setForm({...form, concepto: e.target.value})} required />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Cliente</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.cliente_id} onChange={e => setForm({...form, cliente_id: e.target.value})}>
                <option value="">— Sin asignar —</option>
                {sortedClientes.map(c => <option key={c.id} value={c.id}>{c.nombre_completo}</option>)}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Trastero</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.trastero_id} onChange={e => setForm({...form, trastero_id: e.target.value})}>
                <option value="">— Sin asignar —</option>
                {sortedTrasteros.map(t => <option key={t.id} value={t.id}>{t.codigo} – {t.zona}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Importe (€)" type="number" step="0.01" placeholder="0.00" value={form.importe} onChange={e => setForm({...form, importe: e.target.value})} required />
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Método de pago</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.metodo_pago} onChange={e => setForm({...form, metodo_pago: e.target.value})}>
                <option value="transferencia">Transferencia</option>
                <option value="tpv">TPV</option>
                <option value="efectivo">Efectivo</option>
                <option value="stripe">Stripe</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Fecha vencimiento" type="date" value={form.fecha_vencimiento} onChange={e => setForm({...form, fecha_vencimiento: e.target.value})} />
            <Input label="Fecha de pago" type="date" value={form.fecha_pago} onChange={e => setForm({...form, fecha_pago: e.target.value})} />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text mb-1">Estado</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.estado} onChange={e => setForm({...form, estado: e.target.value})}>
              <option value="pendiente">Pendiente</option>
              <option value="pagado">Pagado</option>
              <option value="atrasado">Atrasado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          <div className="pt-4 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-gray-100 mt-4">
            <Button variant="ghost" onClick={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }} type="button" className="w-full sm:w-auto">Cancelar</Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">{saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar Pago'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Pagos;